import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Avatar, Typography, Chip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatCurrency } from '../../utils/formatters';
import { getProducts, deleteProduct } from '../../api/endpoints/shop';
import AddProductModal from './AddProductModal';

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (catFilter !== 'all') params.category = catFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getProducts(params);
      setProducts(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status !== 404) toast.error(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, catFilter, debouncedSearch]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const categories = [...new Set(products.map((p) => p.category))];

  const handleDelete = async () => {
    try {
      await deleteProduct(deleteId);
      toast.success('Product deleted');
      setDeleteId(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const columns = [
    { id: 'image', label: 'Image', render: (row) => (
      <Avatar src={row.images?.[0]} variant="rounded" sx={{ width: 44, height: 44 }} />
    )},
    { id: 'name', label: 'Product', sortable: true, render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
        <Typography variant="caption" color="text.secondary">{row.category}</Typography>
        {row.isFeatured && <Chip label="Featured" size="small" color="secondary" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />}
      </Box>
    )},
    { id: 'category', label: 'Category' },
    { id: 'price', label: 'Price', align: 'right', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(row.price)}</Typography>
        {row.comparePrice > row.price && (
          <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
            {formatCurrency(row.comparePrice)}
          </Typography>
        )}
      </Box>
    )},
    { id: 'stock', label: 'Stock', align: 'center', render: (row) => (
      <Chip label={row.stock} size="small" color={row.stock > 10 ? 'success' : row.stock > 0 ? 'warning' : 'error'} variant="outlined" />
    )},
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} /> },
    { id: 'actions', label: '', render: (row) => (
      <Stack direction="row">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/shop/products/${row._id}/edit`)}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Products" subtitle="Manage your spiritual products" actionLabel="Add Product" onAction={() => setAddOpen(true)} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search products..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select value={catFilter} label="Category" onChange={(e) => { setCatFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns} rows={products} page={page} rowsPerPage={rowsPerPage}
          total={total} onPageChange={setPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          emptyMessage="No products found"
        />
      )}
      <ConfirmDialog open={!!deleteId} title="Delete Product" message="Are you sure?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      <AddProductModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={fetchProducts} />
    </Box>
  );
};

export default ProductList;
